import { useCallback, useEffect, useRef, useState } from 'react';
import * as orderService from '@/supabase/order/service/orderService';
import { CustomerMonthlyTotals } from '@/supabase/order/types/dashboardSummary';
import { useAuth } from './useAuth';
import { logError } from '@/lib/sentry';

const KST_OFFSET_MS = 9 * 60 * 60 * 1000;

// 선택 가능한 월 개수(당월 포함). 서버가 [KST 당월-2, KST 당월]로 클램프하므로 그 범위와 같아야 한다.
export const CUSTOMER_MONTHLY_TOTALS_MONTH_COUNT = 3;

/**
 * KST 기준 최근 N개월의 'YYYY-MM-01' 목록 (최신이 앞).
 * 브라우저 로컬이 아니라 KST로 계산한다 — 로컬 기준이면 해외 사용자가 월 경계에서
 * 서버가 허용하지 않는 달을 보게 된다.
 */
export function recentKstMonths(count: number = CUSTOMER_MONTHLY_TOTALS_MONTH_COUNT): string[] {
  const nowKst = new Date(Date.now() + KST_OFFSET_MS);
  const year = nowKst.getUTCFullYear();
  const monthIndex = nowKst.getUTCMonth();
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(Date.UTC(year, monthIndex - i, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`;
  });
}

// 결과를 소유 계정과 함께 저장 — 계정 전환 첫 페인트(effect 발화 전)에 이전 계정 데이터가
// 노출되지 않도록 렌더 시점에 userId 일치로 동기 게이트한다 (useDashboardData와 동형).
interface TotalsState {
  userId: string;
  /** 요청한 월이 아니라 서버가 실제 조회한 월 */
  month: string;
  data: CustomerMonthlyTotals;
}

export function useCustomerMonthlyTotals() {
  const { user } = useAuth();
  const uid = user?.id ?? null;

  // localStorage에 저장하지 않는다 — 기간 토글(30/60/90)과 달리 월은 시간이 지나면
  // 3개월 창 밖으로 벗어나 stale해진다. 항상 당월에서 시작하는 편이 단순하다.
  const [months, setMonths] = useState<string[]>(recentKstMonths);
  const [selectedMonth, setSelectedMonth] = useState<string>(() => months[0]);

  // 탭을 열어둔 채 KST 월이 바뀌면 목록이 고정돼 새 달을 고를 수 없다(9월에 연 탭이 10월에도
  // 9/8/7만 보여준다). 서버 클램프는 오래된 쪽만 당겨줄 뿐 누락된 새 달을 복구하지 못한다.
  // 백그라운드 타이머 대신 탭이 다시 보일 때만 확인한다.
  useEffect(() => {
    const sync = () => {
      if (document.visibilityState !== 'visible') return;
      const next = recentKstMonths();
      setMonths((prev) => (prev[0] === next[0] ? prev : next));
      // 선택 월이 새 범위 밖으로 밀렸으면 서버 클램프와 같은 방향(가장 오래된 허용 월)으로 맞춘다.
      setSelectedMonth((prev) => (next.includes(prev) ? prev : next[next.length - 1]));
    };
    document.addEventListener('visibilitychange', sync);
    window.addEventListener('focus', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener('focus', sync);
    };
  }, []);
  const [state, setState] = useState<TotalsState | null>(null);
  const [errorState, setErrorState] = useState<{ userId: string; error: Error } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // 토글 연타 시 늦게 도착한 이전 요청이 최신 결과를 덮지 않도록 하는 stale 가드
  const seqRef = useRef(0);

  const owned = state !== null && state.userId === uid;
  const totals = owned ? state.data : null;
  const loadedMonth = owned ? state.month : null;
  const error = errorState && errorState.userId === uid ? errorState.error : null;

  const fetchTotals = useCallback(async (month: string) => {
    if (!uid) return;
    const seq = ++seqRef.current;
    setIsLoading(true);
    setErrorState(null);
    try {
      const data = await orderService.getCustomerMonthlyTotals(month);
      if (seq !== seqRef.current) return;
      setState({ userId: uid, month: data.month, data });
      // 서버가 범위 밖 월을 클램프했으면 선택 월도 실제 조회 월로 맞춘다.
      // 월 롤오버로 stale해진 탭에서 토글 pill은 "7월"인데 데이터와 라벨은 8월인 모순을 막는다.
      // 같은 월이면 갱신하지 않는다(불필요 렌더 방지).
      if (data.month !== month) setSelectedMonth(data.month);
    } catch (err) {
      if (seq !== seqRef.current) return;
      logError('고객별 구매 총액 조회 실패:', err);
      setErrorState({ userId: uid, error: err as Error });
    } finally {
      if (seq === seqRef.current) setIsLoading(false);
    }
  }, [uid]);

  // 이 카드는 props 0개 자기완결이라 fetch 트리거를 훅이 직접 갖는다
  // (useDashboardData는 callback만 주고 페이지 effect가 호출하는 구조).
  // loadedMonth === selectedMonth 가드가 클램프 직후의 중복 요청을 막는다.
  useEffect(() => {
    if (!uid) return;
    if (loadedMonth === selectedMonth) return;
    void fetchTotals(selectedMonth);
  }, [uid, selectedMonth, loadedMonth, fetchTotals]);

  const refetch = useCallback(() => { void fetchTotals(selectedMonth); }, [fetchTotals, selectedMonth]);

  return {
    totals,
    isLoading,
    error,
    months,
    /** 사용자가 고른 월 (토글 활성 표시용) */
    month: selectedMonth,
    /** 실제로 로드된 데이터의 월 — 숫자 라벨은 반드시 이 값을 써야 한다 */
    loadedMonth,
    setMonth: setSelectedMonth,
    refetch,
  };
}
